import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  type Capacidades,
  type FaqDetalhe,
  type FaqNaLista,
  type ListaFaqs,
  type ResultadoReindexacao,
  ErroApi,
  api,
} from '../lib/api.js';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { EditorFaq } from './EditorFaq.js';
import { BarraDeFiltros, BlocoDeAjuda, Vazio } from './ui.js';

type Status = 'todas' | 'ativa' | 'inativa';

/**
 * FAQ em branco para o formulário de criação.
 *
 * `id` vazio é o que o `EditorFaq` usa para saber que vai CRIAR em vez de
 * salvar — o mesmo formulário serve aos dois caminhos, e dois formulários de
 * FAQ divergiriam no primeiro campo novo.
 */
const FAQ_EM_BRANCO: FaqDetalhe = {
  id: '',
  categoria: 'escola',
  pergunta: '',
  resposta: '',
  variacoes: [],
  ativo: true,
  reindexacaoPendente: false,
  atualizadoEm: new Date().toISOString(),
  trechosIndexados: 0,
  trechosSemEmbedding: [],
};

export function ListaFaq({ capacidades }: { capacidades: Capacidades }): JSX.Element {
  const [dados, setDados] = useState<ListaFaqs | null>(null);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [status, setStatus] = useState<Status>('todas');
  const [soPendentes, setSoPendentes] = useState(false);
  const [ordem, setOrdem] = useState<'atualizacao' | 'pergunta' | 'acionamentos'>('atualizacao');
  const [emEdicao, setEmEdicao] = useState<FaqDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [reindexando, setReindexando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoReindexacao | null>(null);

  const carregar = useCallback(async () => {
    const params = new URLSearchParams();
    if (busca.trim()) params.set('busca', busca.trim());
    if (categoria) params.set('categoria', categoria);
    if (status !== 'todas') params.set('status', status);
    if (soPendentes) params.set('pendentes', 'sim');
    params.set('ordem', ordem);
    params.set('limite', '100');
    try {
      setDados(await api.get<ListaFaqs>(`/admin/faqs?${params.toString()}`));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'falha ao carregar');
    }
  }, [busca, categoria, status, soPendentes, ordem]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function abrir(item: FaqNaLista): Promise<void> {
    setEmEdicao(await api.get<FaqDetalhe>(`/admin/faqs/${item.id}`));
  }

  /**
   * Depois de salvar: fecha e mostra a linha atualizada.
   *
   * FAQ que já existia é remendada NA LISTA, sem recarregar — recarregar
   * trocaria a lista inteira e levaria embora a posição de rolagem de quem
   * estava na linha 40. FAQ NOVA exige recarregar: ela precisa aparecer na
   * posição que a ordenação manda, e não dá para adivinhar qual é.
   */
  function aoSalvarFaq(atualizada: FaqDetalhe, pendentes: number): void {
    const ehNova = !dados?.itens.some((i) => i.id === atualizada.id);
    setEmEdicao(null);

    if (ehNova) {
      void carregar();
      return;
    }

    setDados((atuais) =>
      atuais
        ? {
            ...atuais,
            // `-1` vem da ativação/desativação, que não mexe na fila de
            // reindexação: ali o contador de pendentes não muda.
            pendentesDeReindexacao:
              pendentes >= 0 ? pendentes : atuais.pendentesDeReindexacao,
            itens: atuais.itens.map((i) =>
              i.id === atualizada.id
                ? {
                    ...i,
                    pergunta: atualizada.pergunta,
                    categoria: atualizada.categoria,
                    ativo: atualizada.ativo,
                    reindexacaoPendente: atualizada.reindexacaoPendente,
                    atualizadoEm: atualizada.atualizadoEm,
                    variacoes: atualizada.variacoes.length,
                    trechosIndexados: atualizada.trechosIndexados,
                  }
                : i,
            ),
          }
        : atuais,
    );
  }

  async function reindexar(): Promise<void> {
    setReindexando(true);
    try {
      const r = await api.post<ResultadoReindexacao>('/admin/reindexar');
      setResultado(r);
      notifications.show({
        color: r.faqsComErro > 0 ? 'orange' : 'green',
        message: `Reindexação: ${r.faqsProcessadas} FAQ(s), +${r.embeddingsCriados} embedding(s).`,
      });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'falha ao reindexar');
    } finally {
      setReindexando(false);
    }
  }

  if (!dados) {
    return erro ? (
      <Alert color="red" variant="light" role="alert">
        {erro}
      </Alert>
    ) : (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const pendentes = dados.pendentesDeReindexacao;

  return (
    <Stack gap="sm">
      {/* O estado inconsistente é sempre visível, nunca silencioso. */}
      {pendentes > 0 && (
        <Alert color="violet" variant="light">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm">
              <strong>
                {pendentes} FAQ{pendentes > 1 ? 's' : ''} aguardando reindexação.
              </strong>{' '}
              A busca usa os embeddings antigos até ela rodar.
            </Text>
            {capacidades.administrar ? (
              <Button size="xs" variant="filled" loading={reindexando} onClick={reindexar}>
                Reindexar agora
              </Button>
            ) : (
              <Text size="xs" c="dimmed">
                Só um admin pode reindexar.
              </Text>
            )}
          </Group>
        </Alert>
      )}

      {resultado && (
        <Alert color="gray" variant="light">
          Reindexação: {resultado.faqsProcessadas} FAQ(s), +{resultado.embeddingsCriados}{' '}
          embedding(s), −{resultado.embeddingsRemovidos} órfão(s)
          {resultado.faqsComErro > 0 && (
            <strong> · {resultado.faqsComErro} com erro (seguem pendentes)</strong>
          )}
        </Alert>
      )}

      {capacidades.editar && (
        <Group>
          {/* O estado vazio desta tela já mandava "crie a primeira FAQ em Nova
              FAQ" — e o botão não existia. Agora existe. */}
          <Button variant="filled" size="xs" onClick={() => setEmEdicao(FAQ_EM_BRANCO)}>
            Nova FAQ
          </Button>
        </Group>
      )}

      <BarraDeFiltros>
        <TextInput
          size="xs"
          label="Buscar"
          w={280}
          placeholder="Buscar em pergunta, resposta ou variações"
          value={busca}
          onChange={(e) => setBusca(e.currentTarget.value)}
        />
        <Select
          size="xs"
          label="Categoria"
          w={200}
          value={categoria}
          onChange={(v) => setCategoria(v ?? '')}
          allowDeselect={false}
          data={[
            { value: '', label: 'Todas as categorias' },
            ...dados.categorias.map((c) => ({
              value: c.categoria,
              label: `${c.categoria} (${c.total})`,
            })),
          ]}
        />
        <Select
          size="xs"
          label="Status"
          w={170}
          allowDeselect={false}
          value={status}
          onChange={(v) => setStatus((v ?? 'todas') as Status)}
          data={[
            { value: 'todas', label: 'Ativas e inativas' },
            { value: 'ativa', label: 'Só ativas' },
            { value: 'inativa', label: 'Só inativas' },
          ]}
        />
        <Select
          size="xs"
          label="Ordem"
          w={190}
          allowDeselect={false}
          value={ordem}
          onChange={(v) => setOrdem((v ?? 'atualizacao') as typeof ordem)}
          data={[
            { value: 'atualizacao', label: 'Última atualização' },
            { value: 'pergunta', label: 'Pergunta (A–Z)' },
            { value: 'acionamentos', label: 'Mais acionadas' },
          ]}
        />
        <Checkbox
          size="xs"
          label="Só pendentes"
          checked={soPendentes}
          onChange={(e) => setSoPendentes(e.currentTarget.checked)}
        />
      </BarraDeFiltros>

      {/*
        O aviso de reindexação pendente só existe QUANDO há pendência, e aí já
        é tarde para entender o que ela é. O que decide se uma edição vai ao ar
        na hora ou espera reindexação não está dito em lugar nenhum — e é a
        regra que a secretaria mais precisa. `pendente_conteudo` tem um ⚠️ com
        "conteúdo a preencher", que não conta a parte importante: aquelas FAQs
        transbordam.
      */}
      <BlocoDeAjuda
        secoes={[
          {
            id: 'como',
            titulo: 'Como editar sem quebrar a busca',
            corpo: (
              <>
                <Text size="sm">
                  <strong>Mudar a RESPOSTA vale na hora e não custa nada.</strong> O texto que o
                  aluno recebe é o que está no campo de resposta, literal. Corrigiu um preço, um
                  telefone, uma data? Salvou, está no ar na próxima pergunta. Nenhum aviso aparece,
                  e é isso mesmo.
                </Text>
                <Text size="sm">
                  <strong>Mudar a PERGUNTA ou as VARIAÇÕES exige reindexação.</strong> São elas que
                  o bot usa para <em>encontrar</em> a FAQ, e essa busca trabalha sobre uma versão
                  processada do texto que precisa ser refeita. Até alguém refazer, a busca continua
                  usando a versão antiga — a FAQ responde, mas responde para as perguntas antigas.
                  A tela avisa com um contador no alto e um ⏳ na linha.
                </Text>
                <Text size="sm">
                  <strong>Reindexar é pago e por isso é manual.</strong> Cada pergunta ou variação
                  NOVA custa uma chamada cobrada ao serviço de busca; o que não mudou não é
                  recobrado. Nunca roda sozinho ao salvar — salvar uma vírgula na pergunta não
                  pode gerar fatura sem alguém decidir. Juntar as edições do dia e reindexar uma
                  vez é o caminho. Só admin tem o botão.
                </Text>
                <Text size="sm">
                  <strong>Variações são como o ALUNO escreve, não como o aeroclube fala.</strong> A
                  pergunta canônica pode ser “Qual o valor do curso de Piloto Privado”; as
                  variações são “quanto sai o brevê”, “preço do pp”, “qto custa pra tirar o
                  brevet”. Erro de digitação, abreviação e gíria ajudam — é literalmente o texto
                  que chega pelo WhatsApp. Variação escrita em linguagem de folheto não encontra
                  ninguém.
                </Text>
                <Text size="sm">
                  <strong>Categoria <em>pendente_conteudo</em> (⚠️) é uma FAQ de passagem.</strong>{' '}
                  Ela existe para o bot não ficar calado num assunto que ainda não tem resposta
                  escrita: responde algo genérico e <strong>já passa a conversa para a
                  secretaria</strong>. Enquanto houver FAQ nessa categoria, alguém vai receber
                  transbordo por causa dela — escrever a resposta de verdade e trocar a categoria é
                  o que faz o transbordo parar.
                </Text>
                <Text size="sm" c="dimmed">
                  FAQ desativada (⏸) continua na base e não é encontrada pela busca. É o caminho
                  para tirar uma resposta do ar sem perder o texto.
                </Text>
              </>
            ),
          },
        ]}
      />

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}

      <Text size="sm" c="dimmed">
        {dados.itens.length} de {dados.total}
      </Text>

      <Table.ScrollContainer minWidth={800}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Pergunta</Table.Th>
              <Table.Th>Categoria</Table.Th>
              <Table.Th>Var.</Table.Th>
              <Table.Th>Usos</Table.Th>
              <Table.Th>Veredito</Table.Th>
              <Table.Th>Atualizada</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dados.itens.map((item) => (
              <Table.Tr key={item.id} opacity={item.ativo ? 1 : 0.55}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    {item.reindexacaoPendente && (
                      <Tooltip label="reindexação pendente">
                        <Text size="sm">⏳</Text>
                      </Tooltip>
                    )}
                    {!item.ativo && (
                      <Tooltip label="inativa">
                        <Text size="sm">⏸</Text>
                      </Tooltip>
                    )}
                    <Text size="sm">{item.pergunta}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm">{item.categoria}</Text>
                    {item.categoria === dados.categoriaPendenteConteudo && (
                      <Tooltip label="conteúdo a preencher">
                        <Text size="sm">⚠️</Text>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm">{item.variacoes}</Text>
                    {item.trechosIndexados !== item.variacoes + 1 && (
                      <Tooltip label={`${item.trechosIndexados} indexado(s)`}>
                        <Text size="sm">≠</Text>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{item.acionamentos}</Text>
                </Table.Td>
                {/* Emoji junto da cor: 👍/👎 informam sem depender do tom. */}
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    {item.vereditosPositivos > 0 && (
                      <Text size="sm" c="teal">
                        👍 {item.vereditosPositivos}
                      </Text>
                    )}
                    {item.vereditosNegativos > 0 && (
                      <Text size="sm" c="red" fw={600}>
                        👎 {item.vereditosNegativos}
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {new Date(item.atualizadoEm).toLocaleDateString('pt-BR')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Button size="xs" onClick={() => void abrir(item)}>
                    {capacidades.editar ? 'Editar' : 'Ver'}
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {dados.itens.length === 0 && (
        <Vazio titulo="Nenhuma FAQ neste filtro.">
          {dados.total === 0
            ? 'A base está vazia. Crie a primeira FAQ em “Nova FAQ”: pergunta, resposta literal (é o texto que chega ao aluno, sem reescrita) e variações de como a mesma dúvida é escrita.'
            : 'Limpe a busca ou troque a categoria. Com “Só pendentes” marcado, a lista mostra apenas as FAQs editadas que ainda esperam reindexação.'}
        </Vazio>
      )}

      {/*
        O editor é um MODAL (ver `EditorFaq`): clicar em "Editar" na primeira
        linha abria o formulário depois de 53 linhas de tabela, e quem clicava
        no topo não via nada acontecer.
      */}
      {emEdicao && (
        <EditorFaq
          faq={emEdicao}
          capacidades={capacidades}
          categorias={dados.categorias.map((c) => c.categoria)}
          categoriaPendenteConteudo={dados.categoriaPendenteConteudo}
          aoFechar={() => setEmEdicao(null)}
          aoSalvar={aoSalvarFaq}
        />
      )}
    </Stack>
  );
}
